// src/utils/carteleria/presentacionCarteleria.js
//
// Capa de PRESENTACIÓN de las 3 pantallas de Cartelería (tabla, cabecera, dashboard — F3a). Toma lo que
// dice el motor `calendarioNegocio` (moneda original) y lo convierte con el helper único
// `convertirMontoCarteleria`. NO tiene fórmulas de plata propias: los montos (mensual, por factura,
// totales del plazo, hardware) vienen del motor; acá solo se convierte y se suma.
//
// Estados (D10 / regla 10.113): la facturación mensual estimada cuenta SOLO `Activo` (un 100 % es vendido
// no instalado); el pipeline son 100/90/75/50 %.
import { calendarioNegocio } from "./calendarioNegocio";
import { convertirMontoCarteleria } from "./convertirMontoCarteleria";

export const categoriaEstado = (estado) => {
  const e = (estado || "").toLowerCase();
  return ["activo", "100%", "90%", "75%", "50%"].includes(e) ? e : null;
};
export const esActivoExacto = (estado) => (estado || "").toLowerCase() === "activo";
export const esPipeline = (estado) => ["100%", "90%", "75%", "50%"].includes((estado || "").toLowerCase());

// Con una forma de facturar explícita distinta de `mensual` (cada N meses / única) manda el motor; con
// `mensual` (todo lo existente hoy) sigue valiendo la periodicidad medida contra el puente de DB.
const usaFormaExplicita = (n) => (n.forma_facturacion || "mensual") !== "mensual";

/**
 * Meses que cubre cada factura de gestión de un negocio. `resolverPuente(n)` es `getMesesPorFactura` ya
 * con sus mapas (la pantalla los trae de DB); solo se usa cuando la forma de facturar es `mensual`.
 */
export const mesesPorFacturaCarteleria = (n, resolverPuente, hoy) =>
  usaFormaExplicita(n) ? calendarioNegocio(n, { hoy }).mesesPorFactura : resolverPuente(n);

/**
 * Resumen de la cabecera y del dashboard de una lista de negocios (ya filtrada por país/eliminada).
 * @param {{convertir: Function, monedaLocal: string, hoy: (Date|string)}} ctx `convertir` = `convert` de useCurrency
 */
export const resumenCarteleria = (negocios, { convertir, monedaLocal, hoy }) => {
  const conteo = { activo: 0, "100%": 0, "90%": 0, "75%": 0, "50%": 0 };
  let pipeline = 0;
  let activos = 0;
  let activosOPEX = 0;
  let activosCAPEX = 0;
  let totalPantallas = 0;
  let facturacionMensual = 0;
  (negocios || []).forEach((n) => {
    const cat = categoriaEstado(n.estado);
    if (cat) conteo[cat] += 1;
    if (esPipeline(n.estado)) pipeline += 1;
    if (!esActivoExacto(n.estado)) return;
    activos += 1;
    const cal = calendarioNegocio(n, { hoy });
    if (cal.incluyeHardware && n.modalidad === "OPEX") activosOPEX += 1;
    if (cal.incluyeHardware && n.modalidad === "CAPEX") activosCAPEX += 1;
    totalPantallas += cal.pantallas;
    // Cada componente puede estar en moneda distinta (gestión UF, contenidos CLP, opex USD/PEN): se
    // convierte a moneda local POR SEPARADO antes de sumar (10.62).
    facturacionMensual +=
      convertirMontoCarteleria(convertir, cal.mensual.gestion.monto, cal.monedas.gestion, monedaLocal) +
      convertirMontoCarteleria(convertir, cal.mensual.contenidos.monto, cal.monedas.contenidos, monedaLocal) +
      convertirMontoCarteleria(convertir, cal.mensual.opex.monto, cal.monedas.hardware, monedaLocal);
  });
  return { conteo, activos, pipeline, activosOPEX, activosCAPEX, totalPantallas, facturacionMensual };
};

/**
 * Todas las cifras de plata de una fila de la tabla de Cartelería.
 * @param {object} n fila de `negocios_carteleria`
 * @param {object} ctx `{convertir, monedaLocal, defaultMoneda, hoy, mesesPorFactura?}`
 *   `mesesPorFactura`: periodicidad ya resuelta contra el puente (`getMesesPorFactura`); si la fila trae una
 *   forma de facturar explícita distinta de `mensual`, manda el motor.
 */
export const filaTablaCarteleria = (n, { convertir, monedaLocal, defaultMoneda, hoy, mesesPorFactura }) => {
  const cal = calendarioNegocio(n, { hoy, mesesPorFactura: usaFormaExplicita(n) ? undefined : mesesPorFactura });
  const mg = cal.monedas.gestion;
  const mcCargada = cal.monedas.contenidosCargada;
  const mh = cal.monedas.hardware;
  const monedaPrincipal = mg || defaultMoneda;
  // Contenidos puede estar en moneda distinta a gestión (ej: gestión UF + contenidos CLP): se pasa a la
  // moneda de gestión antes de sumar (10.16). Sin moneda de contenidos cargada se suma tal cual.
  const aMonedaGestion = (monto) => convertirMontoCarteleria(convertir, monto, mcCargada, mg);

  const hardwareMensual = cal.porFactura.opex.monto; // solo OPEX; en moneda de hardware
  // Gestión + contenidos: la conversión de los contenidos a la moneda de gestión ocurre ANTES de multiplicar
  // por pantallas y meses (mismo orden de operaciones que la tabla de hoy → resultado bit a bit idéntico).
  // Con precio TOTAL del contrato (F2) no hay fee por pantalla: se usan los totales del motor.
  const esTotal = cal.precioGestion.modo === "total_contrato";
  const gestionBaseMensual = esTotal
    ? null
    : (cal.porPantalla.gestion.monto + aMonedaGestion(cal.porPantalla.contenidos.monto)) * cal.pantallas;
  const gestionMensual = esTotal
    ? cal.porFactura.gestion.monto + aMonedaGestion(cal.porFactura.contenidos.monto)
    : gestionBaseMensual * cal.porFactura.meses;
  const totalHardwareContrato = cal.totalPlazo.hardwareCapex.total + cal.totalPlazo.opex.monto;
  const totalGestionContrato = esTotal
    ? cal.totalPlazo.gestion.monto + aMonedaGestion(cal.totalPlazo.contenidos.monto)
    : gestionBaseMensual * cal.totalPlazo.meses;

  const totalHardwareEnLocal = convertirMontoCarteleria(convertir, totalHardwareContrato, mh, monedaLocal);
  const totalGestionEnLocal = convertirMontoCarteleria(convertir, totalGestionContrato, mg, monedaLocal);

  return {
    cal,
    cantidadPantallas: cal.pantallas,
    monedaPrincipal,
    monedaGestion: mg,
    monedaHardware: mh,
    // columnas de hardware (valores cargados, en su moneda)
    capex: { monto: cal.hardwareCapex ? cal.hardwareCapex.monto : 0, moneda: mh },
    opexPorPantalla: cal.porPantalla.opex.monto,
    otrosTotal: cal.totalPlazo.hardwareCapex.otros,
    puedeFacturarHw: !!cal.hardwareCapex && cal.hardwareCapex.monto > 0 && cal.modalidad === "CAPEX",
    // gestión y contenidos por pantalla (valores cargados)
    gestionPorPantalla: cal.porPantalla.gestion.monto,
    contenidosPorPantalla: cal.porPantalla.contenidos.monto,
    monedaContenidosVisible: cal.monedas.contenidos,
    // cuota por factura
    hardwareMensual,
    gestionMensual,
    totalMensual: hardwareMensual + gestionMensual,
    // totales del contrato en moneda local
    totalHardwareEnLocal,
    totalGestionEnLocal,
    granTotalEnLocal: totalHardwareEnLocal + totalGestionEnLocal,
  };
};
